import { __awaiter } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAemConfig } from '@as-react/common-components';
import { VoucherActionEnum, VoucherStatus } from '@as-react/common-types';
import { Box } from '@yonderland/andes-react';
import { useEffect, useState } from 'react';
import BasketApi from '../api/basket/BasketAPI';
import CustomerApi from '../api/customer/CustomerApi';
import { HtmlInclude } from '../components/common/html-include/HtmlInclude';
import { getQuerystringByName } from '../util/URLParamUtil';
var VoucherMessageCode;
(function (VoucherMessageCode) {
    VoucherMessageCode["VOUCHER_USED"] = "voucher.used";
    VoucherMessageCode["VOUCHER_INVALID"] = "voucher.invalid";
    VoucherMessageCode["INVALID_CUSTOMER_UUID"] = "invalid.customer.uuid";
    VoucherMessageCode["NO_VOUCHERS_TO_ACTIVATE"] = "no.vouchers.to.activate";
    VoucherMessageCode["ALREADY_ACTIVE"] = "already.active";
})(VoucherMessageCode || (VoucherMessageCode = {}));
const URL_UUID_PARAM_NAME = 'customerUuid';
const URL_RETURN_INCENTIVE_ID_PARAM_NAME = 'returnIncentiveId';
const URL_VOUCHER_ONE_PARAM_NAME = 'voucherId1';
const URL_VOUCHER_TWO_PARAM_NAME = 'voucherId2';
const URL_ACTIVATION_ID_PARAM_NAME = 'activationId';
const DISCOUTNT_VOUCHER_UD_PARAM_NAME = 'discountVoucherId';
const refreshCustomerVouchers = (servicesEndpoint, defaultRequestParams) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        yield BasketApi.refreshCustomerVouchers(servicesEndpoint, defaultRequestParams);
    }
    catch (_a) { }
});
const createVouchers = (uuid, servicesEndpoint, defaultRequestParams) => new Promise((resolve, reject) => {
    const voucher1 = getQuerystringByName(window.location.search, URL_VOUCHER_ONE_PARAM_NAME);
    const voucher2 = getQuerystringByName(window.location.search, URL_VOUCHER_TWO_PARAM_NAME);
    const vouchers = [voucher1, voucher2].filter(Boolean);
    if (vouchers.length === 0 || !uuid) {
        resolve(VoucherStatus.ERROR);
    }
    CustomerApi.postPaybackVouchers(servicesEndpoint, defaultRequestParams, uuid, vouchers).then(() => {
        resolve(VoucherStatus.SUCCESS);
    }, reject);
});
const createReturnIncentiveVoucher = (uuid, servicesEndpoint, defaultRequestParams) => new Promise((resolve, reject) => {
    const returnIncentiveId = getQuerystringByName(window.location.search, URL_RETURN_INCENTIVE_ID_PARAM_NAME);
    if (!returnIncentiveId || !uuid) {
        resolve(VoucherStatus.ERROR);
    }
    CustomerApi.createReturnIncentiveVouchers(servicesEndpoint, defaultRequestParams, uuid, returnIncentiveId).then(() => {
        refreshCustomerVouchers(servicesEndpoint, defaultRequestParams);
        resolve(VoucherStatus.SUCCESS);
    }, reject);
});
const createDiscountVoucher = (uuid, servicesEndpoint, defaultRequestParams) => new Promise((resolve, reject) => {
    const activationId = getQuerystringByName(window.location.search, URL_ACTIVATION_ID_PARAM_NAME);
    const discountVoucherId = getQuerystringByName(window.location.search, DISCOUTNT_VOUCHER_UD_PARAM_NAME);
    if (!activationId || !discountVoucherId || !uuid) {
        resolve(VoucherStatus.ERROR);
    }
    CustomerApi.createDiscountVoucher(servicesEndpoint, defaultRequestParams, activationId, discountVoucherId, uuid).then(() => {
        refreshCustomerVouchers(servicesEndpoint, defaultRequestParams);
        resolve(VoucherStatus.SUCCESS);
    }, reject);
});
const VoucherContainer = ({ voucherAction, loading, success, error: defaultError, alreadyActivated, alreadyUsed, voucherHasExpired, CouldNotBeCreated, }) => {
    const { defaultRequestParams, servicesEndpoint } = useAemConfig();
    const [status, setStatus] = useState(VoucherStatus.LOADING);
    const [uniqueVouchers, setUniqueVouchers] = useState([]);
    useEffect(() => {
        const uuid = getQuerystringByName(window.location.search, URL_UUID_PARAM_NAME);
        if (!uuid) {
            setStatus(VoucherStatus.INVALID);
            setUniqueVouchers([{ messageCode: VoucherMessageCode.INVALID_CUSTOMER_UUID }]);
            return;
        }
        const handleError = (error) => {
            var _a;
            const errData = (_a = error.response) === null || _a === void 0 ? void 0 : _a.data;
            if (!errData || (!errData.vouchers && !errData.code)) {
                setStatus(VoucherStatus.ERROR);
                return;
            }
            setStatus(VoucherStatus.INVALID);
            setUniqueVouchers(errData.vouchers
                ? errData.vouchers
                    .filter(voucher => !voucher.valid)
                    .filter((value, index, array) => array.findIndex(voucher => voucher.messageCode === value.messageCode) === index)
                : errData.code
                    ? [{ messageCode: errData.code }]
                    : []);
        };
        switch (voucherAction) {
            case VoucherActionEnum.ASSIGN_VOUCHER_TO_CUSTOMER:
                createVouchers(uuid, servicesEndpoint, defaultRequestParams).then(setStatus, handleError);
                break;
            case VoucherActionEnum.CREATE_CUSTOMER_RETURN_INCENTIVE:
                createReturnIncentiveVoucher(uuid, servicesEndpoint, defaultRequestParams).then(setStatus, handleError);
                break;
            case VoucherActionEnum.CREATE_DISCOUNT_VOUCHER:
                createDiscountVoucher(uuid, servicesEndpoint, defaultRequestParams).then(setStatus, handleError);
                break;
            default:
        }
    }, []);
    const getVoucherErrorStatus = (voucher) => {
        switch (voucher.messageCode) {
            case VoucherMessageCode.VOUCHER_USED:
                return alreadyUsed;
            case VoucherMessageCode.VOUCHER_INVALID:
                return voucherHasExpired;
            case VoucherMessageCode.INVALID_CUSTOMER_UUID:
            case VoucherMessageCode.NO_VOUCHERS_TO_ACTIVATE:
                return CouldNotBeCreated;
            case VoucherMessageCode.ALREADY_ACTIVE:
                return alreadyActivated;
            default:
                return defaultError;
        }
    };
    return (_jsxs(Box, { paddingBlockStart: { base: '6', md: '8', lg: '6' }, paddingBlockEnd: { lg: '6' }, marginInline: "-2", children: [status === VoucherStatus.LOADING && _jsx(HtmlInclude, { htmlFragment: loading }), status === VoucherStatus.SUCCESS && _jsx(HtmlInclude, { htmlFragment: success }), status === VoucherStatus.ERROR && _jsx(HtmlInclude, { htmlFragment: defaultError }), status === VoucherStatus.INVALID &&
                uniqueVouchers.map(voucher => (_jsx(HtmlInclude, { htmlFragment: getVoucherErrorStatus(voucher) }, voucher.barcode || voucher.messageCode)))] }));
};
export default VoucherContainer;
